/**
 * Pure state helpers for the three company-role dialogs TeamPage owns
 * (`plans/all-team-members-directory.md`, "Company role dialogs").
 *
 * The dialogs are `.vue` files the jest harness cannot mount, so everything
 * they decide lives here: the exact `manageCompanyAdmin` payloads, which of
 * the remove dialog's four bodies applies, and what a callable answer (or the
 * lack of one) says to the user. The components only render and call.
 */
import { MemberRow, MemberTeamRef } from '@/utils/membersApi';

/**
 * The part of a person's row the dialogs read: the teams their memberships
 * are on (the Remove dialog words a pending person's invitations from them)
 * and whether every one is a company-role-only stub.
 */
export type CompanyUserRoleTargetRow = Pick<MemberRow, 'membershipTeams' | 'inviteOnlyStub'>;

/**
 * What the directory row, a team's member dashboard, and the directory's
 * page-level Add button hand TeamPage. `row` is null only for the page-level Add.
 */
export interface CompanyUserRoleTarget {
	row: CompanyUserRoleTargetRow | null;
	email: string;
	userId: string | null;
	companyUserRoleDefId: string | null;
}

export const EMPTY_COMPANY_USER_ROLE_TARGET: CompanyUserRoleTarget = {
	row: null,
	email: '',
	userId: null,
	companyUserRoleDefId: null,
};

/**
 * The target out of an event payload. The payload comes through an untyped
 * `$emit`, so every field is normalised rather than trusted to be present.
 */
export function companyUserRoleTargetFrom(payload: Partial<CompanyUserRoleTarget> | null | undefined): CompanyUserRoleTarget {
	return {
		row: (payload && payload.row) || null,
		email: trimmed(payload && payload.email),
		userId: (payload && payload.userId) || null,
		companyUserRoleDefId: (payload && payload.companyUserRoleDefId) || null,
	};
}

/** The web's copy of the callable's input (`ManageCompanyAdminOnCallData`). */
export interface ManageCompanyAdminPayload {
	email?: string;
	removeUserId?: string;
	companyId: string;
	// null means remove (a live role) or cancel (a pending invite's role).
	companyUserRoleDefId: string | null;
	inviteTeamId?: string;
	// null means "automatically assign".
	inviteTeamUserRoleDefId?: string | null;
}

/**
 * What the callable answers. The onCall wrapper hands back `undefined` data
 * when the use case throws, so every reader takes `| null | undefined`.
 */
export interface ManageCompanyAdminResult {
	success?: boolean;
	message?: string;
	pendingInvite?: boolean;
}

export type ManageCompanyAdminResultData = ManageCompanyAdminResult | null | undefined;

function trimmed(value: string | null | undefined): string {
	return (value || '').trim();
}

/**
 * A page-level Add is the only one opened without a row: an address typed in
 * by hand, which may not be in the company yet.
 */
export function isPageLevelAdd(target: Pick<CompanyUserRoleTarget, 'row'> | null | undefined): boolean {
	return !target || !target.row;
}

/**
 * A pending target has no account, so its company role exists only as a marker
 * on its invite stubs. Cancelling it is keyed by email; a live role is removed
 * by user id.
 */
export function isPendingTarget(target: Pick<CompanyUserRoleTarget, 'userId'> | null | undefined): boolean {
	return !target || !target.userId;
}

export const ADD_DIALOG_BODY: string = 'This action will add the user to the selected role, granting them all associated permissions and notifying them via email.';
export const ADD_DIALOG_PENDING_BODY: string = 'They have not accepted their invitation yet. Unless they already have a RunDiffusion account, the company role activates once they accept, and no new email is sent.';

/**
 * The Add dialog's opening sentence. A listed person with no user id has not
 * accepted anything yet: `manageCompanyAdmin` then only stamps the role on
 * their invite stubs and sends no second email, so "granting them all
 * associated permissions and notifying them via email" would be false. The
 * hedge is for the rare pending person who does have an account, whom the
 * server finds by email and grants the role at once.
 */
export function addDialogBody(target: Pick<CompanyUserRoleTarget, 'row' | 'userId'> | null | undefined): string {
	return !isPageLevelAdd(target) && isPendingTarget(target) ? ADD_DIALOG_PENDING_BODY : ADD_DIALOG_BODY;
}

export interface BuildAssignPayloadInput {
	email: string;
	companyId: string;
	companyUserRoleDefId: string;
	// A page-level Add names a team up front. `manageCompanyAdmin` reads it
	// solely for an address with neither an account nor a pending stub, and
	// nearly everyone already in the directory has one or the other, so a
	// row-level Add starts without one.
	pageLevel: boolean;
	// The exception: a listed person whose account was deleted keeps an
	// ACCEPTED membership, which is neither an account nor a pending stub. The
	// server refuses them for want of a team (`assignNeedsInviteTeam`), and the
	// row-level Add then asks for one and sends it, as the old page always did.
	needsInviteTeam?: boolean;
	inviteTeamId?: string | null;
	inviteTeamUserRoleDefId?: string | null;
	// Choosing the invite's team role is an enterprise benefit; without it
	// the role is always assigned automatically (null).
	isEnterprise?: boolean;
}

/** Whether the Add dialog shows the team picker and sends what it holds. */
export function asksForInviteTeam(input: Pick<BuildAssignPayloadInput, 'pageLevel' | 'needsInviteTeam'>): boolean {
	return !!(input.pageLevel || input.needsInviteTeam);
}

/**
 * `manageCompanyAdmin`'s answer for an address with no account and no pending
 * stub when no `inviteTeamId` came with it. The server keeps this exact string
 * for old clients, so it is the one signal that a team is what is missing.
 */
export const ASSIGN_NEEDS_INVITE_TEAM_SERVER_MESSAGE: string = 'User not found for the provided email';

/**
 * True when a refused Add can be retried with a team. Only a request that sent
 * no team can be refused this way, so the caller asks once: with the picker
 * already showing, the same words are an ordinary refusal to show verbatim.
 */
export function assignNeedsInviteTeam(
	resultData: ManageCompanyAdminResultData,
	input: Pick<BuildAssignPayloadInput, 'pageLevel' | 'needsInviteTeam'>,
): boolean {
	if (asksForInviteTeam(input) || resultSucceeded(resultData)) return false;
	return resultMessage(resultData, '') === ASSIGN_NEEDS_INVITE_TEAM_SERVER_MESSAGE;
}

/**
 * What to say when a row-level Add turns out to need a team: the server's own
 * "User not found" would read as a dead end for a person who is on screen.
 */
export function assignNeedsInviteTeamMessage(email: string, teamDisplayLabel: string): string {
	return `${trimmed(email)} has no RunDiffusion account. Choose the ${trimmed(teamDisplayLabel)} to invite them to, then add the role again.`;
}

export function buildAssignPayload(input: BuildAssignPayloadInput): ManageCompanyAdminPayload {
	const payload: ManageCompanyAdminPayload = {
		email: trimmed(input.email),
		companyId: input.companyId,
		companyUserRoleDefId: input.companyUserRoleDefId,
	};
	if (!asksForInviteTeam(input) || !input.inviteTeamId) return payload;
	return {
		...payload,
		inviteTeamId: input.inviteTeamId,
		inviteTeamUserRoleDefId: input.isEnterprise ? (input.inviteTeamUserRoleDefId || null) : null,
	};
}

export interface BuildChangePayloadInput {
	email: string;
	companyId: string;
	companyUserRoleDefId: string;
}

export function buildChangePayload(input: BuildChangePayloadInput): ManageCompanyAdminPayload {
	return {
		email: trimmed(input.email),
		companyId: input.companyId,
		companyUserRoleDefId: input.companyUserRoleDefId,
	};
}

export interface BuildRemovePayloadInput {
	target: Pick<CompanyUserRoleTarget, 'email' | 'userId'>;
	companyId: string;
}

export function buildRemovePayload(input: BuildRemovePayloadInput): ManageCompanyAdminPayload {
	if (isPendingTarget(input.target)) {
		return {
			email: trimmed(input.target.email),
			companyId: input.companyId,
			companyUserRoleDefId: null,
		};
	}
	return {
		removeUserId: input.target.userId as string,
		companyId: input.companyId,
		companyUserRoleDefId: null,
	};
}

/**
 * The part of a `teams/{teamId}/teamMembers/{email}` doc the remove dialog
 * reads. Structural, so the dialog can pass whatever a one-shot get returned.
 */
export interface CompanyRoleMembershipDoc {
	companyUserRoleDefId?: string | null;
	// True while the invite exists only to carry the company role.
	companyRoleInviteOnly?: boolean | null;
	isAccepted?: boolean | null;
}

/** teamId -> the doc, null (it does not exist), or undefined (never read). */
export type CompanyRoleMembershipsByTeamId = Record<string, CompanyRoleMembershipDoc | null | undefined>;

export interface PendingInviteClassification {
	// The flags decide which body shows; the names are display only. A team
	// with no name would otherwise leave both lists empty and fall through to
	// "the invitation remains active" for a stub that is about to be deleted.
	hasWithdrawnInvites: boolean;
	hasRetainedInvites: boolean;
	withdrawnTeamNames: string[];
	retainedTeamNames: string[];
}

/**
 * What cancelling a pending target's company role does to each invitation,
 * which `manageCompanyAdmin` decides per stub: an invite created only to carry
 * the role is withdrawn entirely (its sign-up link dies), and an ordinary
 * invite that also carries the role is kept without it. A membership that is
 * missing, accepted, or carries no company role is neither.
 *
 * A known, wording-only limit: the teams come from the directory row, which
 * lists ACTIVE teams only, while the server clears or deletes the address's
 * role-carrying stubs on deactivated teams too (the deleted
 * `getPendingCompanyRoleInvites` callable saw those). A stub on a deactivated
 * team is therefore missing from both lists and the body can understate what
 * is withdrawn. The success message does not: `removeOutcome` prefers the
 * server's own words for a pending target, and they count what was withdrawn
 * and what was kept.
 */
export function classifyPendingInvites(
	teams: MemberTeamRef[] | null | undefined,
	membershipsByTeamId: CompanyRoleMembershipsByTeamId | null | undefined,
): PendingInviteClassification {
	const result: PendingInviteClassification = {
		hasWithdrawnInvites: false,
		hasRetainedInvites: false,
		withdrawnTeamNames: [],
		retainedTeamNames: [],
	};
	for (const team of teams || []) {
		const membership = (membershipsByTeamId || {})[team.id];
		if (!membership || !membership.companyUserRoleDefId || membership.isAccepted === true) continue;

		const withdrawn: boolean = membership.companyRoleInviteOnly === true;
		if (withdrawn) result.hasWithdrawnInvites = true;
		else result.hasRetainedInvites = true;

		const name: string = trimmed(team.name);
		const bucket: string[] = withdrawn ? result.withdrawnTeamNames : result.retainedTeamNames;
		if (name && bucket.indexOf(name) === -1) bucket.push(name);
	}
	return result;
}

/**
 * The teams of a pending target worth reading: only a pending membership can
 * carry an invite's company role. Capped so a person invited to thousands of
 * teams costs a bounded number of reads; past the cap the dialog says less
 * rather than reading more.
 */
export const REMOVE_DIALOG_MAX_MEMBERSHIP_READS: number = 50;

export interface PendingInviteReadPlan {
	teams: MemberTeamRef[];
	// True when some pending team was left unread, so the classification
	// cannot be trusted to be complete.
	truncated: boolean;
}

export function pendingInviteReadPlan(row: Pick<MemberRow, 'membershipTeams'> | null | undefined): PendingInviteReadPlan {
	const pending: MemberTeamRef[] = ((row && row.membershipTeams) || []).filter((team) => !!team && !!team.id && team.pending);
	return {
		teams: pending.slice(0, REMOVE_DIALOG_MAX_MEMBERSHIP_READS),
		truncated: pending.length > REMOVE_DIALOG_MAX_MEMBERSHIP_READS,
	};
}

/** The part of a `teams/{teamId}` doc a member dashboard's target row reads. */
export interface CompanyRoleTeamDoc {
	id?: string | null;
	name?: string | null;
	isActive?: boolean | null;
}

/**
 * The target row for a person opened on one team's member dashboard, where the
 * only membership in hand is that team's. It names that team alone, so for a
 * pending person the Remove dialog reads and words that team's invitation
 * only, and someone who also has invitations on other teams is not told what
 * cancelling does to those (the directory's row names every team, so its
 * dialog is). The success message still covers all of them: `removeOutcome`
 * prefers the server's own words for a pending target.
 */
export function companyUserRoleTargetRowFromMembership(
	membership: CompanyRoleMembershipDoc | null | undefined,
	team: CompanyRoleTeamDoc | null | undefined,
): CompanyUserRoleTargetRow {
	if (!membership || !team || !team.id) return { membershipTeams: [], inviteOnlyStub: false };
	const pending: boolean = membership.isAccepted !== true;
	return {
		membershipTeams: [{
			id: team.id,
			name: trimmed(team.name),
			pending,
			isActive: team.isActive !== false,
		}],
		// The flag says every membership the row names is a stub, and this row names one.
		inviteOnlyStub: pending && membership.companyRoleInviteOnly === true,
	};
}

export const REMOVE_DIALOG_COPY_VARIANT = {
	// A live role on an account.
	LIVE: 'live',
	// Pending, and some invitations are withdrawn while others are kept.
	PENDING_WITHDRAWN_AND_RETAINED: 'pendingWithdrawnAndRetained',
	// Pending, and cancelling withdraws the invitation entirely.
	PENDING_WITHDRAWN: 'pendingWithdrawn',
	// Pending, and only the company role goes. Also the generic pending body.
	PENDING_GENERIC: 'pendingGeneric',
} as const;

export type RemoveDialogCopyVariant = typeof REMOVE_DIALOG_COPY_VARIANT[keyof typeof REMOVE_DIALOG_COPY_VARIANT];

export interface RemoveDialogCopyVariantInput {
	isPending: boolean;
	classification: PendingInviteClassification | null | undefined;
	// A membership read failed or was skipped, so `classification` is not the
	// whole picture and is ignored.
	classificationIncomplete?: boolean;
	// The Postgres row's flag: every membership is a company-role-only stub.
	inviteOnlyStub?: boolean;
}

/**
 * Which of the remove dialog's four bodies applies. Without a usable
 * classification a pending target gets the generic pending body, unless the
 * row itself already says every invitation is a company-role-only stub: then
 * "the invitation remains active" would be false, and the withdrawn body
 * (which names no team) is the honest one.
 */
export function removeDialogCopyVariant(input: RemoveDialogCopyVariantInput): RemoveDialogCopyVariant {
	if (!input.isPending) return REMOVE_DIALOG_COPY_VARIANT.LIVE;

	if (input.classificationIncomplete || !input.classification) {
		return input.inviteOnlyStub
			? REMOVE_DIALOG_COPY_VARIANT.PENDING_WITHDRAWN
			: REMOVE_DIALOG_COPY_VARIANT.PENDING_GENERIC;
	}
	if (input.classification.hasWithdrawnInvites && input.classification.hasRetainedInvites) {
		return REMOVE_DIALOG_COPY_VARIANT.PENDING_WITHDRAWN_AND_RETAINED;
	}
	if (input.classification.hasWithdrawnInvites) return REMOVE_DIALOG_COPY_VARIANT.PENDING_WITHDRAWN;
	return REMOVE_DIALOG_COPY_VARIANT.PENDING_GENERIC;
}

/**
 * The names to print for one side of the classification, falling back to the
 * company's own word for a team when none of the teams has a name.
 */
export function inviteTeamsLabel(teamNames: string[] | null | undefined, teamDisplayLabel: string): string {
	return (teamNames || []).join(', ') || `the ${(teamDisplayLabel || '').toLowerCase()}`;
}

/**
 * The message to show for a callable answer: the server's own words when it
 * sent any (every refusal arrives as `{ success: false, message }` and is
 * shown verbatim), otherwise the fallback. Tolerates the `undefined` data the
 * onCall wrapper returns when the use case throws.
 */
export function resultMessage(resultData: ManageCompanyAdminResultData, fallback: string): string {
	const message: string = trimmed(resultData && typeof resultData.message === 'string' ? resultData.message : '');
	return message || fallback;
}

export function resultSucceeded(resultData: ManageCompanyAdminResultData): boolean {
	return !!(resultData && resultData.success === true);
}

/** One snackbar per outcome: whether it worked, and what to say. */
export interface CompanyUserRoleOutcome {
	succeeded: boolean;
	message: string;
}

export const ASSIGN_FAILURE_MESSAGE: string = 'Failed to add company admin role';
// `{ success: false }` that came without a message.
export const CHANGE_FAILURE_MESSAGE: string = 'Failed to update role';
// The call itself failed: a rejected promise, or the `undefined` data the
// onCall wrapper hands back when the use case throws. The old page reached the
// same words both ways (reading `.success` off no data threw into its catch).
export const CHANGE_THROWN_MESSAGE: string = 'Failed to update company admin role';

export function assignOutcome(resultData: ManageCompanyAdminResultData, email: string): CompanyUserRoleOutcome {
	const address: string = trimmed(email);
	if (!resultSucceeded(resultData)) {
		return { succeeded: false, message: resultMessage(resultData, ASSIGN_FAILURE_MESSAGE) };
	}
	return {
		succeeded: true,
		message: resultData && resultData.pendingInvite
			? resultMessage(resultData, `Invited ${address}. The company role activates once they accept.`)
			: `Successfully added company admin role to ${address}`,
	};
}

export interface ChangeOutcomeTarget {
	// The target has a user id: an account exists, so "User not found" cannot
	// mean what it says.
	hasAccount?: boolean;
}

/**
 * What to say when a change for a person WITH an account is refused as "User
 * not found for the provided email": `manageCompanyAdmin` looks the account up
 * by the address it was sent, which is the membership's (invited) address,
 * and `finishTeamMemberSignup` can bind an account whose own address differs
 * from the invited one. The server's words would read as a dead end for a
 * person who is on screen.
 */
export function changeNeedsAccountAddressMessage(email: string): string {
	return `The account behind ${trimmed(email)} uses a different email address than this invitation, so its company role cannot be changed from here.`;
}

export function changeOutcome(
	resultData: ManageCompanyAdminResultData,
	email: string,
	target: ChangeOutcomeTarget | null = null,
): CompanyUserRoleOutcome {
	const address: string = trimmed(email);
	if (!resultData) return { succeeded: false, message: CHANGE_THROWN_MESSAGE };
	if (!resultSucceeded(resultData)) {
		const refusal: string = resultMessage(resultData, CHANGE_FAILURE_MESSAGE);
		if (target && target.hasAccount && refusal === ASSIGN_NEEDS_INVITE_TEAM_SERVER_MESSAGE) {
			return { succeeded: false, message: changeNeedsAccountAddressMessage(address) };
		}
		return { succeeded: false, message: refusal };
	}
	return {
		succeeded: true,
		message: resultData && resultData.pendingInvite
			? resultMessage(resultData, `Updated the pending invite for ${address}`)
			: `Successfully updated role for ${address}`,
	};
}

export interface RemoveOutcomeInput {
	email: string;
	roleName: string;
	isPending: boolean;
}

export function removeFailureMessage(roleName: string): string {
	return `Failed to remove ${roleName} role`;
}

export function removeOutcome(resultData: ManageCompanyAdminResultData, input: RemoveOutcomeInput): CompanyUserRoleOutcome {
	const address: string = trimmed(input.email);
	if (!resultSucceeded(resultData)) {
		return { succeeded: false, message: resultMessage(resultData, removeFailureMessage(input.roleName)) };
	}
	return {
		succeeded: true,
		message: input.isPending
			? resultMessage(resultData, `Canceled the pending ${input.roleName} invite for ${address}`)
			: `Successfully removed ${input.roleName} role from ${address}`,
	};
}

/**
 * One person a bulk company role action from the Directory acts on: their address, their
 * account if they have one, and their company role as the Directory currently shows it (the
 * live role, or the one a pending invite will grant).
 */
export interface BulkCompanyRoleTarget {
	email: string;
	userId: string | null;
	companyUserRoleDefId: string | null;
	// The row's own answer to "holds a company role", which covers the moment before its live
	// role has been read (the Postgres flag carries it until then).
	hasCompanyRole: boolean;
}

export const BULK_COMPANY_ROLE_MODE = {
	ASSIGN: 'assign',
	REMOVE: 'remove',
} as const;

export type BulkCompanyRoleMode = typeof BULK_COMPANY_ROLE_MODE[keyof typeof BULK_COMPANY_ROLE_MODE];

export interface BulkCompanyRolePlanInput {
	mode: BulkCompanyRoleMode;
	// The role to assign; ignored when removing.
	companyUserRoleDefId?: string | null;
	viewerUserId?: string | null;
	viewerIsAdmin?: boolean;
}

export interface BulkCompanyRolePlan {
	// Who will be sent to manageCompanyAdmin, each address once.
	targets: BulkCompanyRoleTarget[];
	// The viewer's own row, left alone: only a RunDiffusion admin may change their own company role.
	skippedSelf: number;
	// People it would not change: already holding the role being assigned, or holding none to remove.
	skippedUnchanged: number;
}

/**
 * Who a bulk action actually changes. Each address once. Never the viewer's own role unless
 * they are a RunDiffusion admin, which is the rule TeamPage applies to the one-person version.
 * And nobody it would not change, so a bulk Assign skips the role's holders (the server would
 * refuse each one) and a bulk Remove skips people who hold none.
 */
export function planBulkCompanyRole(
	targets: BulkCompanyRoleTarget[] | null | undefined,
	input: BulkCompanyRolePlanInput,
): BulkCompanyRolePlan {
	const plan: BulkCompanyRolePlan = { targets: [], skippedSelf: 0, skippedUnchanged: 0 };
	const seen: Record<string, true> = {};
	for (const target of targets || []) {
		const email: string = trimmed(target && target.email).toLowerCase();
		if (!email || seen[email]) continue;
		seen[email] = true;

		const userId: string | null = target.userId || null;
		if (userId && input.viewerUserId && userId === input.viewerUserId && !input.viewerIsAdmin) {
			plan.skippedSelf++;
			continue;
		}

		const current: string | null = target.companyUserRoleDefId || null;
		const unchanged: boolean = input.mode === BULK_COMPANY_ROLE_MODE.ASSIGN
			? !!current && current === (input.companyUserRoleDefId || null)
			: !current && !target.hasCompanyRole;
		if (unchanged) {
			plan.skippedUnchanged++;
			continue;
		}
		plan.targets.push({ email, userId, companyUserRoleDefId: current, hasCompanyRole: !!(current || target.hasCompanyRole) });
	}
	return plan;
}

function peopleCount(count: number): string {
	return `${count} ${count === 1 ? 'person' : 'people'}`;
}

/** What the dialog says it is about to do, before anyone confirms. */
export function bulkCompanyRolePlanSummary(plan: BulkCompanyRolePlan, mode: BulkCompanyRoleMode): string {
	const parts: string[] = [];
	if (plan.targets.length) {
		parts.push(mode === BULK_COMPANY_ROLE_MODE.ASSIGN
			? `This changes ${peopleCount(plan.targets.length)}.`
			: `This removes the company role from ${peopleCount(plan.targets.length)}.`);
	} else {
		parts.push(mode === BULK_COMPANY_ROLE_MODE.ASSIGN
			? 'Everyone selected already has this role.'
			: 'No one selected has a company role.');
	}
	if (plan.targets.length && plan.skippedUnchanged) {
		parts.push(mode === BULK_COMPANY_ROLE_MODE.ASSIGN
			? `${peopleCount(plan.skippedUnchanged)} already ${plan.skippedUnchanged === 1 ? 'has' : 'have'} it.`
			: `${peopleCount(plan.skippedUnchanged)} ${plan.skippedUnchanged === 1 ? 'has' : 'have'} none to remove.`);
	}
	if (plan.skippedSelf) parts.push('Your own company role stays as it is.');
	return parts.join(' ');
}

/** One call's answer, kept per address so a partial failure can say who and why. */
export interface BulkCompanyRoleResult {
	email: string;
	succeeded: boolean;
	message: string;
}

export interface BulkCompanyRoleOutcome extends CompanyUserRoleOutcome {
	// How many people were changed; the caller refreshes the Directory when any were.
	changed: number;
}

/** One snackbar for the whole run: what changed, and the first refusal in the server's own words. */
export function bulkCompanyRoleOutcome(
	results: BulkCompanyRoleResult[],
	input: { mode: BulkCompanyRoleMode; roleName: string },
): BulkCompanyRoleOutcome {
	const changed: number = results.filter((result) => result.succeeded).length;
	const failed: BulkCompanyRoleResult[] = results.filter((result) => !result.succeeded);
	const done: string = input.mode === BULK_COMPANY_ROLE_MODE.ASSIGN
		? `Assigned ${trimmed(input.roleName) || 'the company role'} to ${peopleCount(changed)}`
		: `Removed the company role from ${peopleCount(changed)}`;
	if (!failed.length) return { succeeded: true, changed, message: done };

	const refusal: string = `${peopleCount(failed.length)} could not be changed. ${failed[0].email}: ${failed[0].message}`;
	return {
		succeeded: false,
		changed,
		message: changed ? `${done}. ${refusal}` : refusal,
	};
}
